import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, PencilSimple, Trash, Warning, ArrowClockwise, MagnifyingGlass, User, FilePdf, ShoppingCart } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ArchiveButton } from "@/components/ArchiveButton";
import { downloadFile } from "@/lib/api";

const CATEGORIES = [
  { key: "uniform",   label: "Uniform" },
  { key: "footwear",  label: "Footwear / Shoes" },
  { key: "equipment", label: "Equipment / Tools" },
  { key: "it",        label: "IT (Laptop / Phone)" },
  { key: "other",     label: "Other" },
];
const CONDITIONS = [
  { key: "good",      label: "Good",      tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  { key: "damaged",   label: "Damaged",   tone: "bg-amber-50 text-amber-800 border-amber-200" },
  { key: "lost",      label: "Lost",      tone: "bg-rose-50 text-rose-800 border-rose-200" },
  { key: "returned",  label: "Returned",  tone: "bg-blue-50 text-blue-800 border-blue-200" },
  { key: "replaced",  label: "Replaced",  tone: "bg-indigo-50 text-indigo-800 border-indigo-200" },
];
const CONDITION_LABEL = Object.fromEntries(CONDITIONS.map((c) => [c.key, c]));

const EMPTY_ITEM = { name: "", category: "equipment", quantity: 1, unit: "pcs", unit_cost: 0, issued_date: new Date().toISOString().slice(0, 10), condition: "good", remarks: "" };
const EMPTY_FORM = { employee_name: "", employee_id: "", position: "", department: "", notes: "", items: [{ ...EMPTY_ITEM }] };

export default function AccountabilityPage() {
  const { user } = useAuth();
  const canEdit = can(user, ["admin", "procurement", "inventory"]);
  const [forms, setForms] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const [conditionTarget, setConditionTarget] = useState(null); // {form, item}
  const [ct, setCt] = useState({ condition: "damaged", remarks: "" });

  async function load() {
    setLoading(true);
    try {
      const params = search ? { employee: search } : {};
      const { data } = await api.get("/accountability", { params });
      setForms(data);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search]);

  function openCreate() { setEditing(null); setForm({ ...EMPTY_FORM, items: [{ ...EMPTY_ITEM }] }); setOpen(true); }
  function openEdit(f) {
    setEditing(f);
    setForm({
      employee_name: f.employee_name || "", employee_id: f.employee_id || "",
      position: f.position || "", department: f.department || "",
      notes: f.notes || "",
      items: (f.items || []).map((it) => ({ ...EMPTY_ITEM, ...it })),
    });
    setOpen(true);
  }
  function addRow() { setForm((f) => ({ ...f, items: [...f.items, { ...EMPTY_ITEM }] })); }
  function delRow(i) { setForm((f) => ({ ...f, items: f.items.filter((_, ix) => ix !== i) })); }
  function updateRow(i, patch) { setForm((f) => ({ ...f, items: f.items.map((it, ix) => (ix === i ? { ...it, ...patch } : it)) })); }

  async function submitForm(e) {
    e.preventDefault();
    if (!form.employee_name.trim()) { toast.error("Employee name is required"); return; }
    if (!form.items.length) { toast.error("Add at least one item"); return; }
    const payload = {
      ...form,
      items: form.items.map((it) => ({ ...it, quantity: parseInt(it.quantity) || 1, unit_cost: Number(it.unit_cost) || 0 })),
    };
    try {
      if (editing) { await api.put(`/accountability/${editing.id}`, payload); toast.success("Form updated"); }
      else { await api.post("/accountability", payload); toast.success("Accountability form created"); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed"); }
  }

  async function submitCondition(e) {
    e.preventDefault();
    if (!conditionTarget) return;
    try {
      await api.post(`/accountability/${conditionTarget.form.id}/items/${conditionTarget.item.id}/condition`, ct);
      toast.success(`Marked as ${ct.condition}`);
      setConditionTarget(null); setCt({ condition: "damaged", remarks: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed"); }
  }

  return (
    <div className="space-y-6" data-testid="accountability-page">
      <div className="flex items-end justify-between border-b border-border pb-6">
        <div>
          <div className="section-caption mb-2">Inventory</div>
          <h1 className="section-title text-3xl sm:text-4xl">Employee Accountability</h1>
          <p className="text-sm text-muted-foreground mt-1">Every uniform, tool and laptop issued to staff — track condition, log damage, request replacements.</p>
        </div>
        {canEdit && (
          <Button onClick={openCreate} data-testid="new-accountability-btn"><Plus size={16} className="mr-2" /> New Form</Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-sm">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by employee name…"
            className="pl-9 border-border"
            data-testid="accountability-search-input"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground p-6">Loading…</div>
      ) : forms.length === 0 ? (
        <div className="border border-border rounded-md p-8 text-center text-sm text-muted-foreground">
          No accountability forms yet. Click <span className="font-medium">New Form</span> to record the first one.
        </div>
      ) : forms.map((f) => (
        <div key={f.id} className="border border-border rounded-md bg-card overflow-hidden" data-testid={`accountability-form-${f.id}`}>
          <div className="p-4 border-b border-border flex items-start justify-between gap-4">
            <div>
              <div className="section-caption">{f.form_no}</div>
              <div className="flex items-center gap-2 mt-1">
                <User size={16} className="text-muted-foreground" />
                <div className="font-heading font-bold tracking-tight text-lg">{f.employee_name}</div>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {f.position && <span>{f.position} · </span>}
                {f.department && <span>{f.department} · </span>}
                {f.employee_id && <span>ID: {f.employee_id}</span>}
              </div>
              {f.notes && <div className="text-xs text-muted-foreground mt-2 max-w-xl">{f.notes}</div>}
            </div>
            {canEdit && (
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" className="border-border" onClick={async () => {
                  try {
                    await downloadFile(`/accountability/${f.id}/pdf`, `${f.form_no}.pdf`);
                    toast.success("Downloaded PDF");
                  } catch { toast.error("Download failed"); }
                }} data-testid={`print-accountability-${f.id}`}>
                  <FilePdf size={14} className="mr-1" /> Print PDF
                </Button>
                <Button size="sm" variant="outline" className="border-border" onClick={() => openEdit(f)} data-testid={`edit-accountability-${f.id}`}>
                  <PencilSimple size={14} className="mr-1" /> Edit
                </Button>
                <ArchiveButton endpoint={`/accountability/${f.id}`} label={f.form_no} onArchived={load} testId={`archive-accountability-${f.id}`} />
              </div>
            )}
            {!canEdit && (
              <Button size="sm" variant="outline" className="border-border" onClick={async () => {
                try {
                  await downloadFile(`/accountability/${f.id}/pdf`, `${f.form_no}.pdf`);
                  toast.success("Downloaded PDF");
                } catch { toast.error("Download failed"); }
              }} data-testid={`print-accountability-${f.id}`}>
                <FilePdf size={14} className="mr-1" /> Print PDF
              </Button>
            )}
          </div>
          <div className="overflow-x-auto">
            <Table className="min-w-[1100px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead>Issued</TableHead>
                  <TableHead className="text-right">Unit Cost</TableHead>
                  <TableHead>Condition</TableHead>
                  <TableHead>Remarks</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(f.items || []).map((it) => {
                  const cond = CONDITION_LABEL[it.condition || "good"] || CONDITION_LABEL.good;
                  return (
                    <TableRow key={it.id} data-testid={`acc-item-${it.id}`}>
                      <TableCell className="font-medium">{it.name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{CATEGORIES.find((c) => c.key === it.category)?.label || "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">{it.quantity}</TableCell>
                      <TableCell className="text-sm">{it.unit}</TableCell>
                      <TableCell className="font-mono text-xs">{it.issued_date}</TableCell>
                      <TableCell className="text-right tabular-nums">₱{Number(it.unit_cost || 0).toLocaleString()}</TableCell>
                      <TableCell>
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${cond.tone}`}>{cond.label}</span>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[220px] truncate" title={it.remarks || ""}>{it.remarks || "—"}</TableCell>
                      <TableCell className="text-right">
                        {canEdit && (
                          <div className="flex justify-end gap-1">
                            {(it.condition === "damaged" || it.condition === "lost") && !it.replacement_request_no && (
                              <Button size="sm" variant="outline" className="border-border h-7 bg-amber-50 text-amber-900 hover:bg-amber-100" onClick={async () => {
                                if (!window.confirm(`Create a replacement Budget Request for ${it.name} (qty ${it.quantity})?`)) return;
                                try {
                                  const { data } = await api.post(`/accountability/${f.id}/items/${it.id}/replacement-request`);
                                  toast.success(`Created ${data.request_no}`);
                                  load();
                                } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
                              }} data-testid={`replace-${it.id}`} title="Auto-generate a Budget Request for this replacement">
                                <ShoppingCart size={12} className="mr-1" /> Replace
                              </Button>
                            )}
                            {it.replacement_request_no && (
                              <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded border bg-indigo-50 text-indigo-800 border-indigo-200 whitespace-nowrap" title={`Auto-generated replacement request: ${it.replacement_request_no}`} data-testid={`replace-chip-${it.id}`}>
                                {it.replacement_request_no}
                              </span>
                            )}
                            <Button size="sm" variant="outline" className="border-border h-7" onClick={() => { setConditionTarget({ form: f, item: it }); setCt({ condition: it.condition === "good" ? "damaged" : "good", remarks: "" }); }} data-testid={`condition-${it.id}`}>
                              <Warning size={12} className="mr-1" /> Mark
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      ))}

      {/* Create / Edit form */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl" data-testid="accountability-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black">{editing ? `Edit ${editing.form_no}` : "New Accountability Form"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitForm} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Employee Name *</Label>
                <Input required value={form.employee_name} onChange={(e) => setForm({ ...form, employee_name: e.target.value })} className="border-border" data-testid="acc-employee-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Employee ID</Label>
                <Input value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="border-border" data-testid="acc-employee-id-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Position</Label>
                <Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="border-border" data-testid="acc-position-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Department</Label>
                <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} className="border-border" data-testid="acc-department-input" />
              </div>
            </div>

            <div className="pt-2 border-t border-border">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Items *</Label>
                <Button type="button" size="sm" variant="outline" className="border-border" onClick={addRow} data-testid="acc-add-item-btn">
                  <Plus size={12} className="mr-1" /> Add item
                </Button>
              </div>
              <div className="space-y-2 max-h-[340px] overflow-y-auto">
                {form.items.map((it, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-start border border-border rounded-md p-2">
                    <Input className="col-span-3 border-border" placeholder="Item name" value={it.name} onChange={(e) => updateRow(i, { name: e.target.value })} required data-testid={`acc-item-name-${i}`} />
                    <Select value={it.category} onValueChange={(v) => updateRow(i, { category: v })}>
                      <SelectTrigger className="col-span-2 border-border h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input className="col-span-1 border-border" type="number" min="1" value={it.quantity} onChange={(e) => updateRow(i, { quantity: e.target.value })} />
                    <Input className="col-span-1 border-border" placeholder="unit" value={it.unit} onChange={(e) => updateRow(i, { unit: e.target.value })} />
                    <Input className="col-span-2 border-border" type="date" value={it.issued_date} onChange={(e) => updateRow(i, { issued_date: e.target.value })} />
                    <Input className="col-span-2 border-border" type="number" step="0.01" placeholder="cost" value={it.unit_cost} onChange={(e) => updateRow(i, { unit_cost: e.target.value })} />
                    <Button type="button" variant="ghost" size="sm" className="col-span-1 h-9 text-rose-700" onClick={() => delRow(i)} disabled={form.items.length === 1} data-testid={`acc-item-remove-${i}`}>
                      <Trash size={14} />
                    </Button>
                    <Input className="col-span-12 border-border" placeholder="Remarks (optional)" value={it.remarks} onChange={(e) => updateRow(i, { remarks: e.target.value })} />
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border-border" placeholder="Any handover / condition notes…" data-testid="acc-notes-input" />
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="acc-submit-btn">{editing ? "Save" : "Create form"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change condition */}
      <Dialog open={!!conditionTarget} onOpenChange={(v) => !v && setConditionTarget(null)}>
        <DialogContent data-testid="condition-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black">Update Condition — {conditionTarget?.item?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitCondition} className="space-y-3">
            <div className="text-xs text-muted-foreground">
              Assigned to <span className="font-medium text-foreground">{conditionTarget?.form?.employee_name}</span>. Current condition: <span className="uppercase tracking-widest">{conditionTarget?.item?.condition || "good"}</span>.
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">New Condition</Label>
              <Select value={ct.condition} onValueChange={(v) => setCt({ ...ct, condition: v })}>
                <SelectTrigger data-testid="condition-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CONDITIONS.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Remarks</Label>
              <Textarea rows={2} value={ct.remarks} onChange={(e) => setCt({ ...ct, remarks: e.target.value })} placeholder="e.g. Sole detached during field work — needs replacement" className="border-border" data-testid="condition-remarks-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setConditionTarget(null)}>Cancel</Button>
              <Button type="submit" data-testid="condition-submit-btn"><ArrowClockwise size={14} className="mr-2" /> Update</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
